import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import moment from "moment";

import ContentLayout from "Components/ContentLayout";
import config from "Constants/api_config";
import useDecodedParams from "Hooks/useDecodedParams";
import useQueryParams from "Hooks/useQueryStrings";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { palette as colorPalette } from "Libs/themes/legacyThemes";
import { environmentSelector } from "Reducers/environment";
import {
  selectFeatureDeploymentOverlayEnabled,
  selectFeatureProfilingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import getAccessToken from "src/organization/pages/project/environment/metrics/api/getAccessToken";
import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import useUpdateQueryParams from "src/organization/pages/project/environment/metrics/hooks/useUpdateQueryParams";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { concatenateUrls } from "src/organization/pages/project/environment/metrics/utils/concatenateUrls";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";
import { ParseTimeDurationType } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/types";
import { trackEventPendo } from "Store/analytics/pendoTracker";
import { useAppSelector } from "Store/hooks";

import { ApplicationType } from "../type";

import * as S from "./Page.styles";

type WidgetType = {
  addEventListener(eventName: string, event: (event: any) => void): void;
  updateFilter(arg: { [x: string]: any }): void;
  setShowDeployments(showDeployments: boolean): void;
};

const QUERY_PARAMS = ["fromA", "fromB", "toA", "toB", "compare"];

const getComparedRange = (queryObject: { [x: string]: string | null }) => {
  if (!queryObject) return;
  const { fromA, fromB, toA, toB } = queryObject;
  return {
    fromA: Number(fromA),
    toA: Number(toA),
    fromB: Number(fromB),
    toB: Number(toB)
  };
};

declare global {
  interface Window {
    blackfire: {
      renderContinuousProfiling: (ref: unknown, options: unknown) => WidgetType;
    };
  }
}
type Props = {
  app: ApplicationType;
  profile?: string;
  children: React.ReactElement<ProfilerInjectProps>;
};

export type ProfilerInjectProps<E = object> = {
  timeframe: TimeFrameType;
  setTimeframe: (t: TimeFrameType) => void;
  poll: ParseTimeDurationType;
  lastUpdated?: Date;
  app: ApplicationType;
  isVisible: boolean;
  profile?: string;
  deploymentProps: {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
  };
} & E;

const ProfilingWrapper = (props: Props) => {
  const { app, children } = props;

  const ref = useRef(null);
  const initialTimeframe = useInitialTimeframe({ id: "1h" });
  const isDeploymentOverlayEnabled = useAppSelector(
    selectFeatureDeploymentOverlayEnabled
  );

  const [deployment, setDeployment] = useState(isDeploymentOverlayEnabled);

  const [timeframe, setTimeframe] = useState<TimeFrameType>(initialTimeframe);
  const [widget, setWidget] = useState<WidgetType>();
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [apps, setApps] = useState<{ name: string; profiles: string }[]>();
  const [profile, setProfile] = useState(props.profile);

  const { compare, ...queryParams } = useQueryParams(QUERY_PARAMS);

  const [enableCompare, setEnableCompare] = useState(Boolean(compare));
  const [compareRange, setCompareRange] = useState(
    getComparedRange(queryParams)
  );

  const { update } = useUpdateQueryParams(
    {
      profile,
      compare: enableCompare ?? "true"
    },
    true
  );

  const { projectId } = useDecodedParams<{ projectId: string }>();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const profilingEnabled = useAppSelector(selectFeatureProfilingEnabled);
  const poll = useMemo(
    () => parseTimeDuration(defaultPollTime[timeframe?.id || "fallback"]),
    [timeframe?.id]
  );

  const range = timeframeToRange(timeframe);

  const refreshWidget = (update?: {
    to: number;
    from: number;
    profile?: string;
    application?: string;
  }) => {
    if (widget && update) {
      widget.updateFilter({
        ...update,
        comparison: enableCompare,
        compareRange
      });
      setLastUpdated(new Date());
    }
  };

  useEffect(() => {
    if (compareRange) {
      update(compareRange);
      setCompareRange(compareRange);
    }
  }, [compareRange, update]);

  const onCompareChange = ({ detail }) => {
    setEnableCompare(detail.comparison);
    if (!detail.comparison) {
      update({ fromA: false, fromB: false, toA: false, toB: false }, true);
      setCompareRange(undefined);
    }
    trackEventPendo("comparison_toggle", {
      value: detail.comparison
    });
  };

  const handleDeployment = (enabled: boolean) => {
    setDeployment(enabled);
    if (widget) widget.setShowDeployments(enabled);
  };

  useEffect(() => {
    if (timeframe?.id) {
      const from = moment(range.start).unix();
      const to = moment(range.end).unix();
      const interval = setInterval(() => {
        refreshWidget({
          from,
          to,
          profile
        });
      }, poll.seconds * 1000);
      return () => {
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, timeframe?.id, poll.seconds, widget, profile, enableCompare]);

  useEffect(() => {
    const application = app.name;
    const from = moment(range.start).unix();
    const to = moment(range.end).unix();
    refreshWidget({
      from,
      to,
      profile,
      application
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, profile, apps, app.name, enableCompare]);

  useLayoutEffect(() => {
    if (
      !profilingEnabled ||
      !window.blackfire ||
      !ref.current ||
      !projectId ||
      !environment?.machine_name
    )
      return;

    const activitiesLink = environment?.getLinks()?.["#activities"]?.href;
    const activitiesEndpoint = activitiesLink
      ? concatenateUrls(config.api_url, activitiesLink)
      : null;

    const widget = window.blackfire.renderContinuousProfiling(ref.current, {
      projectId,
      branchMachineName: environment.machine_name,
      activitiesEndpoint,
      jwtCallback: async () => {
        const result = await getAccessToken();
        return result.access_token;
      },
      colorPalette
    });

    const e = event => {
      const apps = event.detail?.applications;
      if (apps) setApps(apps);
      widget.setShowDeployments(deployment);
    };

    widget.addEventListener?.("applicationload", e);
    widget.addEventListener?.("comparisonmodechange", onCompareChange);
    widget.addEventListener?.("comparisonrangechange", e =>
      setCompareRange(e.detail)
    );
    widget.addEventListener?.("profiletypechange", p =>
      setProfile(p?.detail.profile_type || profile)
    );
    setWidget(widget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.machine_name, profilingEnabled, projectId]);

  const deploymentProps = {
    enabled: deployment,
    onChange: handleDeployment
  };

  return (
    <ContentLayout>
      {React.cloneElement(children, {
        timeframe,
        setTimeframe,
        poll,
        lastUpdated,
        deploymentProps,
        isVisible: true,
        app
      })}
      <S.FlameChartLayout isVisible>
        <div ref={ref} id="blackfire-snippet" />
      </S.FlameChartLayout>
    </ContentLayout>
  );
};

export default ProfilingWrapper;
